// The keyboard shortcut list, which is also where they are rebound. Every key
// the app listens for is in lib/shortcuts.ts, so this dialog is the whole
// surface: no action can exist here that the app doesn't honour.
import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/dialog";
import { Plus, RotateCcw, X } from "lucide-react";
import { useShortcuts } from "@/hooks/useShortcuts";
import {
  DEFAULT_BINDINGS, SHORTCUTS, conflictsWith, eventCombo, formatCombo, matches, type ShortcutId,
} from "@/lib/shortcuts";

const GROUPS = [...new Set(SHORTCUTS.map((s) => s.group))];
const LABELS = Object.fromEntries(SHORTCUTS.map((s) => [s.id, s.label])) as Record<ShortcutId, string>;

export function ShortcutsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { bindings, setBinding, resetAll } = useShortcuts();
  const [recording, setRecording] = useState<ShortcutId | null>(null);
  const [clash, setClash] = useState<{ id: ShortcutId; with: ShortcutId[] } | null>(null);

  useEffect(() => {
    if (!open) { setRecording(null); setClash(null); }
  }, [open]);

  // Dismiss closes this dialog like it closes the palette — but not while a
  // key is being recorded, where it cancels the recording instead.
  useEffect(() => {
    if (!open || recording) return;
    const onKey = (e: KeyboardEvent) => { if (matches(e, bindings.dismiss)) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, recording, bindings, onClose]);

  // While recording, this listener eats the keystroke before anything else can
  // act on it — otherwise binding "/" would also jump to the filter box.
  useEffect(() => {
    if (!recording) return;
    const onKey = (e: KeyboardEvent) => {
      const combo = eventCombo(e);
      if (!combo) return;
      e.preventDefault();
      e.stopPropagation();
      if (combo === "escape") { setRecording(null); return; }
      const taken = conflictsWith(bindings, combo, recording);
      setClash(taken.length ? { id: recording, with: taken } : null);
      setBinding(recording, [...bindings[recording].filter((c) => c !== combo), combo]);
      setRecording(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [recording, bindings, setBinding]);

  return (
    <Modal open={open} onClose={onClose} title="Keyboard shortcuts" wide
      footer={
        <button onClick={() => { resetAll(); setClash(null); }}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-[13px] text-muted-foreground hover:bg-secondary cursor-pointer">
          <RotateCcw size={13} /> Reset all to defaults
        </button>
      }>
      <p className="text-[13px] text-muted-foreground mb-4">
        Click a key to change it, then press the combination you want. Shortcuts follow you to any browser you sign in from.
      </p>
      <div className="space-y-5">
        {GROUPS.map((group) => (
          <div key={group}>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1.5">{group}</div>
            <div className="divide-y divide-border border border-border rounded-md">
              {SHORTCUTS.filter((s) => s.group === group).map((s) => {
                const combos = bindings[s.id];
                const changed = combos.join("|") !== DEFAULT_BINDINGS[s.id].join("|");
                return (
                  <div key={s.id} className="flex items-start gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium">{s.label}</div>
                      <div className="text-[12px] text-muted-foreground">{s.description}</div>
                      {clash?.id === s.id && (
                        <div className="text-[12px] text-amber-500 mt-0.5">
                          Also used by {clash.with.map((id) => LABELS[id]).join(", ")}.
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-wrap justify-end shrink-0">
                      {combos.map((combo) => (
                        <span key={combo} className="inline-flex items-center gap-1 rounded border border-border bg-secondary px-1.5 py-0.5 font-mono text-[12px]">
                          {formatCombo(combo)}
                          {combos.length > 1 && (
                            <button aria-label={`Remove ${formatCombo(combo)} from ${s.label}`}
                              onClick={() => setBinding(s.id, combos.filter((c) => c !== combo))}
                              className="text-muted-foreground hover:text-foreground cursor-pointer">
                              <X size={11} />
                            </button>
                          )}
                        </span>
                      ))}
                      <button aria-label={`Add a key for ${s.label}`} title="Add a key"
                        onClick={() => { setClash(null); setRecording(s.id); }}
                        className="h-6 w-6 grid place-items-center rounded border border-border text-muted-foreground hover:bg-secondary hover:text-foreground cursor-pointer">
                        {recording === s.id ? <span className="text-[10px] animate-pulse">…</span> : <Plus size={12} />}
                      </button>
                      {changed && (
                        <button aria-label={`Reset ${s.label}`} title="Reset to default"
                          onClick={() => { setClash(null); setBinding(s.id, s.defaults); }}
                          className="h-6 w-6 grid place-items-center rounded border border-border text-muted-foreground hover:bg-secondary hover:text-foreground cursor-pointer">
                          <RotateCcw size={11} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {recording && (
        <div className="mt-4 text-[13px] text-muted-foreground">
          Press the key for <span className="font-medium text-foreground">{LABELS[recording]}</span> — Esc to cancel.
        </div>
      )}
    </Modal>
  );
}
